import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import CloseIcon from "@mui/icons-material/Close";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getRightworkAPI } from "../requests/utilities/requests";
import { API_get_learning_form_summaries } from "../../../constants";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 520,
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  border: "none",
};

function AddFormToSectionModal(props) {
  const [selectedForm, setSelectedForm] = React.useState("");
  const [formsData, setFormsData] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);

  const fetchFormsData = async () => {
    setIsLoading(true);
    try {
      const res = await getRightworkAPI(API_get_learning_form_summaries, {
        company_id: props.homeCompany,
      });
      if (res.status === 200) {
        setFormsData(res.data.redux?.payload?.learning_forms || []);
      }
    } catch (error) {
      toast.error("Couldn't load forms. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (props.open) {
      setSelectedForm("");
      fetchFormsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  return (
    <Modal open={props.open} onClose={props.handleClose}>
      <Box sx={style}>
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">Add a form</h3>
          <button
            className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500"
            onClick={props.handleClose}
          >
            <CloseIcon />
          </button>
        </div>

        <div className="px-6 py-5">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <LoadSpinner />
            </div>
          ) : (formsData || []).length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">
              No forms yet. Use "Create Form" to build one.
            </p>
          ) : (
            <>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Select a form
              </label>
              <Select
                fullWidth
                value={selectedForm}
                displayEmpty
                onChange={(e) => setSelectedForm(e.target.value)}
                sx={{
                  borderRadius: "8px",
                  height: 44,
                  // Match the app's violet (MUI Selects default to blue).
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#a78bfa" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
                }}
              >
                <MenuItem disabled value="">
                  <span className="text-gray-500">Select a form</span>
                </MenuItem>
                {(formsData || []).map((form) => (
                  <MenuItem key={form.uuid} value={form}>
                    {form.name || "Untitled form"}
                    <span className="ml-2 text-xs text-gray-400">
                      {form.field_count} field
                      {form.field_count === 1 ? "" : "s"}
                    </span>
                  </MenuItem>
                ))}
              </Select>
            </>
          )}
        </div>

        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
          <button
            className="px-5 py-2 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium text-sm"
            onClick={props.handleClose}
          >
            Cancel
          </button>
          <button
            className={`px-5 py-2 rounded-lg font-medium text-sm ${
              selectedForm
                ? "bg-violet-600 hover:bg-violet-700 text-white"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
            disabled={!selectedForm}
            onClick={() => {
              props.handleFormSelect(selectedForm);
              props.handleClose();
            }}
          >
            Add Form
          </button>
        </div>
      </Box>
    </Modal>
  );
}

export default AddFormToSectionModal;
